import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import FormatAlignCenterRoundedIcon from '@mui/icons-material/FormatAlignCenterRounded'
import FormatAlignLeftRoundedIcon from '@mui/icons-material/FormatAlignLeftRounded'
import FormatAlignRightRoundedIcon from '@mui/icons-material/FormatAlignRightRounded'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import MoneyOffCsredRoundedIcon from '@mui/icons-material/MoneyOffCsredRounded'
import PercentRoundedIcon from '@mui/icons-material/PercentRounded'
import PinRoundedIcon from '@mui/icons-material/PinRounded'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { ITableCellStyle } from '../../../definitions/report_builder/ReportBuilderDefinitions'
import ColorPicker from './ColorPicker'

interface P {
    style?: ITableCellStyle
    onChange: (style: ITableCellStyle) => void
    label?: string
    formatNumbers?: boolean
    onFormatNumberChanges?: (formatNumber: boolean) => void
    onFormatPercentageChanges?: (formatNumber: boolean) => void
    onFormatNumbersNoDollarChanges?: (formatNumber: boolean) => void
    formatNumbersNoDollar?: boolean
    formatNumbersPercentage?: boolean
    numberFormatter?: boolean
    onResetStyle?: () => void
    textAlign?: boolean
    headersStyle?: boolean

}

const TextTableEditor = ({ style, onChange, label, formatNumbers, onFormatNumberChanges, onFormatPercentageChanges, formatNumbersPercentage, numberFormatter, onFormatNumbersNoDollarChanges, formatNumbersNoDollar, onResetStyle, textAlign, headersStyle }: P) => {
    const iconSize = '30px'

    const handleTextColorChange = (color: string) => {
        if (color) {
            onChange({ ...style, color })
        } else {
            const { ...restStyle } = style
            onChange(restStyle)
        }
    }

    const handleBackgroundColorChange = (color: string) => {
        if (color) {
            onChange({ ...style, background: color })
        } else {
            const { ...restStyle } = style
            onChange(restStyle)
        }
    }

    return (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant='caption' sx={{ minWidth: 20, mb: 1, display: 'block' }}>
                    {label}:
                </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                <Tooltip title='Bold' placement='top'>
                    <IconButton
                        size='small'
                        onClick={() => onChange({ ...style, bold: !style.bold })}
                        sx={{
                            'height': iconSize,
                            'width': iconSize,
                            'border': 1,
                            'borderColor': style.bold ? 'primary.main' : 'divider',
                            'borderRadius': 1,
                            '&:hover': {
                                borderColor: style.bold ? 'primary.light' : 'action.hover'
                            }
                        }}>
                        <FormatBoldIcon fontSize='medium' />
                    </IconButton>
                </Tooltip>

                <Tooltip title='Strikethrough' placement='top'>
                    <IconButton
                        onClick={() => onChange({ ...style, strike: !style.strike })}
                        sx={{
                            'display': 'flex',
                            'alignItems': 'center',
                            'justifyContent': 'center',
                            'height': iconSize,
                            'width': iconSize,
                            'border': 1,
                            'borderColor': style.strike ? 'primary.main' : 'divider',
                            'borderRadius': 1,
                            '&:hover': {
                                borderColor: style.strike ? 'primary.light' : 'action.hover'
                            }
                        }}>
                        <StrikethroughSIcon fontSize='medium' />
                    </IconButton>
                </Tooltip>
                <ColorPicker
                    type='text'
                    color={style.color}
                    onColorChange={handleTextColorChange}
                    tooltip='Text Color'
                />

                <ColorPicker
                    type='background'
                    color={style.background}
                    onColorChange={handleBackgroundColorChange}
                    tooltip='Background Color'
                />
            </Box>
            {headersStyle && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title='Text align left' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'left' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'left' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'left' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignLeftRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align center' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'center' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'center' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'center' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignCenterRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align right' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'right' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'right' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'right' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignRightRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            {numberFormatter && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tooltip title='Financial Format $$$' placement='top'>
                        <IconButton
                            onClick={() => onFormatNumberChanges(!formatNumbers)}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': formatNumbers ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: formatNumbers ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <MoneyOffCsredRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Format with locale en-US' placement='top'>
                        <IconButton
                            onClick={() => onFormatNumbersNoDollarChanges(!formatNumbersNoDollar)}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': formatNumbersNoDollar ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: formatNumbersNoDollar ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <PinRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Percentage Format' placement='top'>
                        <IconButton
                            onClick={() => onFormatPercentageChanges(!formatNumbersPercentage)}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': formatNumbersPercentage ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: formatNumbersPercentage ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <PercentRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Reset Text Style' placement='top'>
                        <IconButton
                            onClick={() => {
                                onChange({})
                                if (onFormatNumberChanges) onFormatNumberChanges(false)
                                if (onFormatPercentageChanges) onFormatPercentageChanges(false)
                                if (onFormatNumbersNoDollarChanges) onFormatNumbersNoDollarChanges(false)
                                if (onResetStyle) onResetStyle()
                            }}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: 'action.hover'
                                }
                            }}>
                            <CloseRoundedIcon fontSize='medium' color='error' />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            {textAlign && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Tooltip title='Text align left' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'left' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'left' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'left' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignLeftRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align center' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'center' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'center' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'center' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignCenterRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title='Text align right' placement='top'>
                        <IconButton
                            onClick={() => onChange({ ...style, alignment: 'right' })}
                            sx={{
                                'height': iconSize,
                                'width': iconSize,
                                'border': 1,
                                'borderColor': style.alignment === 'right' ? 'primary.main' : 'divider',
                                'borderRadius': 1,
                                '&:hover': {
                                    borderColor: style.alignment === 'right' ? 'primary.light' : 'action.hover'
                                }
                            }}>
                            <FormatAlignRightRoundedIcon fontSize='medium' />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

        </Box>
    )
}

export default TextTableEditor
